<template>
  <section class="config-section">
    <div class="header-group">
      <h2 class="main-title">{{ data.title }}</h2>
      <div class="ethos-card">
        <div class="card-inner">
          <div class="brand-header">
            <div class="b-item">
              <span class="label">SINCE 1995</span>
              <span class="val">ABR® Legacy</span>
            </div>
            <div class="plus">+</div>
            <div class="b-item">
              <span class="label">MODERN STANDARD</span>
              <span class="val">YOLEE® Service</span>
            </div>
          </div>
          <p class="brand-desc">{{ data.brandContext }}</p>
          
          <div class="standard-bar">
            <div class="bar-title">Universal Manufacturing Standard</div>
            <div class="bar-content">
              <strong>Gantry Machining Center</strong>
              <span class="dot"></span>
              <span>100% In-house CNC Milling</span>
              <span class="dot"></span>
              <span>Unified QC Protocol</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="selection-guide" v-if="data.naming">
      <h3 class="guide-title">{{ data.naming.title }}</h3>
      <p class="guide-desc">{{ data.naming.description }}</p>
    </div>

    <div class="tier-grid">
      <div 
        v-for="(item, index) in data.items" 
        :key="index" 
        class="tier-card"
        :class="{ 
          'is-popular': item.isBestSeller,
          'is-pro': item.isPremium,
          'is-u': item.model.includes('(U)'),
          'is-legacy': item.model.includes('(E)')
        }"
      >
        <div v-if="item.isBestSeller" class="tier-tag popular">Market Favorite</div>
        <div v-if="item.isPremium" class="tier-tag pro">Global Standard</div>
        <div v-if="item.model.includes('(U)')" class="tier-tag safety">Max Safety</div>
        <div v-if="item.model.includes('(E)')" class="tier-tag legacy">Classic</div>
        
        <div class="card-main">
          <div class="head">
            <span class="code">{{ item.model }}</span>
            <h4 class="name">{{ item.grade }}</h4>
          </div>
          <p class="tagline">{{ item.tagline }}</p>
          <ul class="features">
            <li v-for="(hl, i) in item.highlights" :key="i">{{ hl }}</li>
          </ul>
        </div>

        <div class="card-footer">
          <span v-if="item.model.includes('(E)')">Steel Plate Welded Frame</span>
          <span v-else>New-Gen Hybrid Steel Architecture</span>
        </div>
      </div>
    </div>
    
    <p class="footnote" v-if="data.note">{{ data.note }}</p>
  </section>
</template>

<script>
export default {
  name: "ConfigurationOverview",
  props: {
    data: { type: Object, default: () => ({}) }
  }
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";

.config-section {
  margin: 80px auto;
  max-width: 1200px;

  .header-group {
    margin-bottom: 50px;
    .main-title { font-size: 32px; color: @color-text-title; font-weight: 800; margin-bottom: 30px; }
    .ethos-card {
      background: @color-bg-gray;
      padding: 40px;
      border-radius: @radius-base;
      border: 1px solid #dee5e1;
      .brand-header {
        display: flex; gap: 30px; align-items: center; margin-bottom: 20px;
        .b-item {
          .label { font-size: 10px; font-weight: 700; color: @color-tips; letter-spacing: 1px; }
          .val { font-size: 22px; font-weight: 800; color: @color-primary; display: block; }
        }
        .plus { font-size: 24px; color: @color-primary; opacity: 0.3; }
      }
      .brand-desc { font-size: 16px; line-height: 1.7; color: @color-text-content; margin-bottom: 30px; }
      .standard-bar {
        background: #fff; border: 1px solid #e0e6e2; display: flex; border-radius: 4px; overflow: hidden;
        .bar-title { background: @color-primary; color: #fff; padding: 10px 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; display: flex; align-items: center; }
        .bar-content { padding: 10px 25px; font-size: 13px; color: @color-text-sub; display: flex; align-items: center; gap: 15px;
          strong { color: @color-primary; }
          .dot { width: 4px; height: 4px; background: @color-primary; border-radius: 50%; opacity: 0.3; }
        }
      }
    }
  }

  .selection-guide {
    margin-bottom: 40px;
    .guide-title { font-size: 20px; color: @color-text-title; font-weight: 700; margin-bottom: 8px; }
    .guide-desc { font-size: 15px; color: @color-text-sub; }
  }

  .tier-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: @breakpoint-mobile) { grid-template-columns: 1fr; }

    .tier-card {
      background: #fff; border: 1px solid #eee; padding: 30px; display: flex; flex-direction: column; position: relative; transition: all 0.3s ease;
      
      &.is-legacy { background: #fafafa; }
      &.is-popular { border-color: @color-primary; background: linear-gradient(to bottom, #fff, fade(@color-bg-light, 20%)); }
      &.is-pro { border-color: @color-primary; border-width: 2px; box-shadow: 0 10px 30px rgba(58, 95, 73, 0.1); }
      &.is-u { border-color: #333; }

      .tier-tag {
        position: absolute; top: -11px; left: 20px; padding: 2px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border-radius: 2px;
        &.popular { background: @color-tips; color: #fff; }
        &.pro { background: @color-primary; color: #fff; }
        &.safety { background: #333; color: #fff; }
        &.legacy { background: #999; color: #fff; }
      }

      .head { margin-bottom: 20px; .code { font-size: 11px; color: @color-tips; font-weight: 800; } .name { font-size: 19px; color: @color-text-title; margin: 0; } }
      .tagline { font-size: 13px; color: @color-text-content; line-height: 1.5; min-height: 60px; margin-bottom: 25px; }
      .features {
        list-style: none; padding: 0; margin: 0;
        li { font-size: 12.5px; color: @color-text-sub; margin-bottom: 10px; padding-left: 15px; position: relative;
          &::before { content: ""; position: absolute; left: 0; top: 8px; width: 6px; height: 1px; background: @color-primary; }
        }
      }
      .card-footer { margin-top: auto; padding-top: 20px; border-top: 1px dashed #ddd; font-size: 10px; font-weight: 700; color: @color-primary; text-transform: uppercase; }
    }
  }

  .footnote { margin-top: 30px; font-size: 13px; color: @color-tips; text-align: center; font-style: italic; }
}
</style>